import { useEffect, useMemo, useState } from "react";
import { Plus, Send, Lock, Repeat } from "lucide-react";
import {
  api, formatApiError, CLASSIFICATION_LABELS, PRIORITY_LABELS, TASK_STATUS_LABELS,
  CLASS_STYLE, PRIO_STYLE,
} from "@/lib/api";
import { toast } from "sonner";
import ColumnPicker from "@/components/ColumnPicker";

const QUICK = [
  { id: "all", label: "Todos" },
  { id: "essential", label: "Essenciais" },
  { id: "ed", label: "E + D" },
  { id: "q", label: "Q" },
  { id: "noassignee", label: "Sem resp." },
  { id: "nosprint", label: "Sem sprint" },
  { id: "overdue", label: "Atrasadas" },
];

// Backlog: master list of tasks NOT on the Kanban (in_kanban=false). Sending a task
// to the Kanban moves the same entity (no copy) and removes it from this view.
export default function Backlog({ projectId, project, sprints, columns, refreshKey, onOpenTask, onCreateTask, onChanged }) {
  const [tasks, setTasks] = useState(null);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({ classification: "", priority: "", assignee: "", sprint: "", sort: "created" });
  const [quick, setQuick] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [picker, setPicker] = useState(null);

  const load = async () => {
    try {
      const params = { on_board: "false" };
      if (filters.classification) params.classification = filters.classification;
      if (filters.priority) params.priority = filters.priority;
      if (filters.assignee) params.assignee = filters.assignee;
      if (filters.sprint) params.sprint = filters.sprint;
      const { data } = await api.get(`/projects/${projectId}/tasks`, { params });
      setTasks(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.classification, filters.priority, filters.assignee, filters.sprint, refreshKey]);

  const filtered = useMemo(() => {
    if (!tasks) return null;
    const q = query.trim().toLowerCase();
    let out = q ? tasks.filter((t) => t.title.toLowerCase().includes(q)) : [...tasks];
    if (quick === "essential") out = out.filter((t) => t.classification === "E");
    if (quick === "ed") out = out.filter((t) => ["E", "D"].includes(t.classification));
    if (quick === "q") out = out.filter((t) => t.classification === "Q");
    if (quick === "noassignee") out = out.filter((t) => !t.assignee_id);
    if (quick === "nosprint") out = out.filter((t) => !t.sprint_id);
    if (quick === "overdue") out = out.filter((t) => t.due_date && new Date(t.due_date) < new Date() && t.status === "open");
    if (filters.sort === "priority") {
      const rank = { urgent: 0, high: 1, medium: 2, low: 3 };
      out.sort((a, b) => (rank[a.priority] ?? 9) - (rank[b.priority] ?? 9));
    } else if (filters.sort === "due") {
      out.sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"));
    }
    return out;
  }, [tasks, query, quick, filters.sort]);

  const toggleSel = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };
  const clearSel = () => setSelected(new Set());

  const sendSingle = (id) => setPicker({ ids: [id], single: true });
  const sendBatch = () => setPicker({ ids: [...selected] });

  const doSend = async (columnId) => {
    const ids = picker.ids;
    try {
      if (picker.single) { await api.post(`/tasks/${ids[0]}/send-to-kanban`, { column_id: columnId }); toast.success("Tarefa enviada ao Kanban."); }
      else { const { data } = await api.post(`/projects/${projectId}/tasks/send-to-kanban`, { task_ids: ids, column_id: columnId }); toast.success(`${data.sent.length} enviada(s) ao Kanban.${data.skipped.length ? ` ${data.skipped.length} ignorada(s).` : ""}`); }
      setPicker(null); clearSel(); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const overdue = (t) => t.due_date && new Date(t.due_date) < new Date() && t.status === "open";
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("pt-BR") : "—";

  return (
    <div data-testid="backlog-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{project.workspace_name}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2">Backlog</h1>
          <p className="text-slate-500 mt-1">Lista mestre de tarefas. Classifique, priorize, envie ao Kanban ou a uma sprint.</p>
        </div>
        {project.can_create_task !== false && project.status === "active" && (
          <button onClick={() => onCreateTask()} data-testid="backlog-create-task" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"><Plus className="w-4 h-4" /> Nova tarefa</button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-4 space-y-3" data-testid="backlog-filters">
        <div className="flex flex-wrap gap-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar..." data-testid="backlog-search" className="h-9 px-3 rounded-lg border border-slate-200 text-sm flex-1 min-w-[160px]" />
          <select value={filters.classification} onChange={(e) => setFilters({ ...filters, classification: e.target.value })} data-testid="bf-classification" className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
            <option value="">Todo escopo</option>{Object.entries(CLASSIFICATION_LABELS).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
          </select>
          <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} data-testid="bf-priority" className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
            <option value="">Toda prioridade</option>{Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filters.assignee} onChange={(e) => setFilters({ ...filters, assignee: e.target.value })} data-testid="bf-assignee" className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
            <option value="">Todos</option><option value="me">Minhas</option><option value="none">Sem responsável</option>
          </select>
          <select value={filters.sprint} onChange={(e) => setFilters({ ...filters, sprint: e.target.value })} data-testid="bf-sprint" className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
            <option value="">Todas sprints</option><option value="none">Sem sprint</option>{(sprints || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={filters.sort} onChange={(e) => setFilters({ ...filters, sort: e.target.value })} data-testid="bf-sort" className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
            <option value="created">Criação</option><option value="priority">Prioridade</option><option value="due">Prazo</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {QUICK.map((c) => (
            <button key={c.id} onClick={() => setQuick(c.id)} data-testid={`backlog-quick-${c.id}`}
              className={`h-7 px-3 rounded-md text-xs font-medium transition-colors ${quick === c.id ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"}`}>{c.label}</button>
          ))}
        </div>
      </div>

      {selected.size > 0 && (
        <div className="bg-slate-900 text-white rounded-lg px-4 py-2 mb-3 flex items-center gap-3 text-sm" data-testid="backlog-batch-bar">
          <span>{selected.size} selecionada(s)</span>
          <button onClick={sendBatch} disabled={columns.length === 0} data-testid="backlog-batch-send" className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-white text-slate-900 text-xs font-medium disabled:opacity-50"><Send className="w-3.5 h-3.5" /> Enviar ao Kanban</button>
          <button onClick={clearSel} className="text-xs text-slate-300 hover:text-white ml-auto">Limpar</button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="backlog-section">
        {filtered === null ? (
          <div className="p-8 text-center text-sm text-slate-500">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500" data-testid="backlog-empty">Nenhuma tarefa no backlog.</div>
        ) : (
          <table className="w-full text-sm" data-testid="backlog-list">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3 w-8"></th>
                <th className="px-2 py-3">Tarefa</th>
                <th className="px-2 py-3 w-20">Escopo</th>
                <th className="px-2 py-3 w-24">Prioridade</th>
                <th className="px-2 py-3 w-36">Responsável</th>
                <th className="px-2 py-3 w-28">Prazo</th>
                <th className="px-2 py-3 w-28">Sprint</th>
                <th className="px-2 py-3 w-16">Pontos</th>
                <th className="px-2 py-3 w-28"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50/60" data-testid={`backlog-row-${t.id}`}>
                  <td className="px-5 py-3"><input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSel(t.id)} data-testid={`backlog-check-${t.id}`} /></td>
                  <td className="px-2 py-3 cursor-pointer" onClick={() => onOpenTask(t.id)}>
                    <div className="font-medium text-slate-900 flex items-center gap-1.5">
                      {t.visibility !== "project" && <Lock className="w-3 h-3 text-violet-500" />}
                      {t.recurrence_id && <Repeat className="w-3 h-3 text-violet-500" title="Tarefa recorrente" data-testid={`backlog-recurring-${t.id}`} />}
                      {t.title}
                      {overdue(t) && <span className="text-[10px] font-bold uppercase tracking-wide text-rose-600">Atrasada</span>}
                    </div>
                  </td>
                  <td className="px-2 py-3"><span className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-semibold ${CLASS_STYLE[t.classification]}`} title={CLASSIFICATION_LABELS[t.classification]}>{t.classification}</span></td>
                  <td className="px-2 py-3"><span className={`inline-flex items-center gap-1 text-xs ${t.priority === "urgent" || t.priority === "high" ? "text-orange-600" : "text-blue-600"}`}><span className="w-1.5 h-1.5 rounded-full bg-current" />{PRIORITY_LABELS[t.priority]}</span></td>
                  <td className="px-2 py-3 text-slate-600">{t.assignee_name || (t.available_to_claim ? <span className="text-emerald-600">Disponível</span> : "—")}</td>
                  <td className={`px-2 py-3 ${overdue(t) ? "text-rose-600" : "text-slate-600"}`}>{fmtDate(t.due_date)}</td>
                  <td className="px-2 py-3">{t.sprint_name ? <span className="text-xs px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: t.sprint_color || "#64748b" }}>{t.sprint_name}</span> : <span className="text-slate-400">—</span>}</td>
                  <td className="px-2 py-3 text-slate-600">{t.story_points ?? "—"}</td>
                  <td className="px-2 py-3 text-right">
                    {t.can_edit !== false && columns.length > 0 && (
                      <button onClick={() => sendSingle(t.id)} data-testid={`backlog-send-${t.id}`} className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-slate-200 text-xs hover:bg-slate-50">→ Kanban</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {picker && <ColumnPicker columns={columns} count={picker.single ? null : picker.ids.length} onPick={doSend} onClose={() => setPicker(null)} />}
    </div>
  );
}
